import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, Key, ShieldCheck, ShieldAlert, ShieldQuestion, Package, MapPin, ChevronRight } from "lucide-react";
import { useMemberAuth, memberApi } from "../lib/memberAuth";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { MembershipSection } from "./MemberDashboard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";

export default function AccountDashboard() {
  const { account, member, application, isMember, idStatus, logout, refresh } = useMemberAuth();
  const [orders, setOrders] = useState([]);

  const loadOrders = useCallback(async () => {
    try {
      const r = await memberApi.get("/account/orders");
      setOrders(r.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { if (account) loadOrders(); }, [account, loadOrders]);

  if (!account) return null;

  return (
    <section className="min-h-screen bg-[#0A0A0A]" data-testid="account-dashboard">
      <header className="border-b border-[var(--twr-line)] px-6 sm:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <Link to="/"><Logo variant="horizontal" /></Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/55 hover:text-[var(--twr-bronze)] transition-colors">View Site</Link>
          {account.auth_provider !== "google" && <ChangePasswordDialog />}
          <button onClick={logout} data-testid="account-signout" className="inline-flex items-center gap-2 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors">
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
        <p className="eyebrow">Your Account</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight" data-testid="account-welcome">
          {account.full_name?.split(" ")[0] || "Welcome"}, good to see you.
        </h1>

        <div className="grid lg:grid-cols-2 gap-6 mt-12">
          <ProfileCard account={account} onSaved={refresh} />
          <IdVerificationCard idStatus={idStatus} idv={account.id_verification} onChanged={refresh} />
        </div>

        <OrdersSection orders={orders} />

        <div className="mt-16" data-testid="account-membership-section">
          <p className="eyebrow">Membership</p>
          {isMember ? (
            <div className="mt-6"><MembershipSection /></div>
          ) : application ? (
            <div className="surface p-8 mt-6 max-w-2xl" data-testid="account-application-status">
              <p className="font-serif text-2xl">Your application is in.</p>
              <p className="text-[var(--twr-warm-white)]/60 text-sm mt-3">
                Submitted {new Date(application.created_at).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" })} — {application.membership_type} tier.
              </p>
              <p className="mt-5">
                <span className={`text-[11px] tracking-[0.22em] uppercase px-3 py-1.5 border ${application.status === "approved" ? "border-emerald-400 text-emerald-400" : application.status === "rejected" ? "border-red-400 text-red-400" : "border-amber-400 text-amber-400"}`}>
                  Status: {application.status}
                </span>
              </p>
              <p className="text-[var(--twr-warm-white)]/45 text-[12px] mt-5">Applications are reviewed by the Founding Committee in confidential waves.</p>
            </div>
          ) : (
            <div className="surface p-8 mt-6 max-w-2xl" data-testid="account-apply-cta">
              <p className="font-serif text-2xl">Not yet a member.</p>
              <p className="text-[var(--twr-warm-white)]/60 text-sm mt-3">Membership unlocks the lounge, lockers, events, and Win Points on every dollar spent.</p>
              <Link to="/membership" className="btn-bronze mt-6 inline-flex">Explore Membership <ChevronRight size={14} /></Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ProfileCard({ account, onSaved }) {
  const addr = account.shipping_address || {};
  const [form, setForm] = useState({
    full_name: account.full_name || "", phone: account.phone || "",
    line1: addr.line1 || "", line2: addr.line2 || "", city: addr.city || "", state: addr.state || "", zip: addr.zip || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await memberApi.put("/account/profile", {
        full_name: form.full_name,
        phone: form.phone,
        shipping_address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, zip: form.zip },
      });
      toast.success("Profile saved.");
      onSaved();
    } catch (err) { toast.error("Could not save profile"); }
    finally { setBusy(false); }
  };

  const inputCls = "mt-2 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-2.5 text-sm";

  return (
    <div className="surface p-7" data-testid="account-profile-card">
      <div className="flex items-center gap-2 text-[var(--twr-bronze)]">
        <MapPin size={14} strokeWidth={1.5} />
        <p className="eyebrow">Profile & Shipping</p>
      </div>
      <form onSubmit={save} className="mt-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="eyebrow">Full Name</span>
            <input data-testid="profile-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="eyebrow">Phone</span>
            <input data-testid="profile-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></label>
        </div>
        <label className="block"><span className="eyebrow">Email</span>
          <input disabled value={account.email} className={`${inputCls} opacity-50`} /></label>
        <label className="block"><span className="eyebrow">Address Line 1</span>
          <input data-testid="profile-line1" value={form.line1} onChange={(e) => set("line1", e.target.value)} className={inputCls} /></label>
        <label className="block"><span className="eyebrow">Address Line 2</span>
          <input value={form.line2} onChange={(e) => set("line2", e.target.value)} className={inputCls} /></label>
        <div className="grid grid-cols-3 gap-4">
          <label className="block"><span className="eyebrow">City</span>
            <input data-testid="profile-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="eyebrow">State</span>
            <input value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="eyebrow">ZIP</span>
            <input value={form.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} /></label>
        </div>
        <button disabled={busy} data-testid="profile-save" className="btn-bronze">{busy ? "Saving…" : "Save Profile"}</button>
      </form>
    </div>
  );
}

function IdVerificationCard({ idStatus, idv, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image must be under 6 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await memberApi.post("/account/id-verification", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("ID submitted — pending review. You can shop right away.");
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const meta = {
    none: { Icon: ShieldQuestion, cls: "text-[var(--twr-warm-white)]/55 border-[var(--twr-line-strong)]", label: "Not verified" },
    pending: { Icon: ShieldAlert, cls: "text-amber-400 border-amber-400/50", label: "Pending review" },
    approved: { Icon: ShieldCheck, cls: "text-emerald-400 border-emerald-400/50", label: "Verified 21+" },
    rejected: { Icon: ShieldAlert, cls: "text-red-400 border-red-400/50", label: "Rejected — re-upload" },
  }[idStatus] || { Icon: ShieldQuestion, cls: "", label: idStatus };
  const Icon = meta.Icon;

  return (
    <div className="surface p-7" data-testid="account-id-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[var(--twr-bronze)]">
          <ShieldCheck size={14} strokeWidth={1.5} />
          <p className="eyebrow">Age Verification (21+)</p>
        </div>
        <span data-testid="id-status-chip" className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 border ${meta.cls}`}>
          <Icon size={12} /> {meta.label}
        </span>
      </div>
      <p className="text-[var(--twr-warm-white)]/60 text-sm mt-5 leading-relaxed">
        Florida law requires us to confirm you are 21 or older before selling cigars or accepting a membership
        application. Upload a photo of your government-issued ID once — it stays securely on file.
      </p>
      {idStatus === "rejected" && idv?.note && (
        <p className="text-red-400/90 text-[13px] mt-3">Reviewer note: {idv.note}</p>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="id-upload-input" />
      {idStatus !== "approved" && (
        <button disabled={busy} onClick={() => fileRef.current?.click()} data-testid="id-upload-btn" className="btn-bronze mt-6">
          {busy ? "Uploading…" : idStatus === "none" ? "Upload Your ID" : "Upload a New Photo"}
        </button>
      )}
      {idStatus !== "none" && (
        <p className="text-[var(--twr-warm-white)]/45 text-[12px] mt-4">
          Uploaded {idv?.uploaded_at ? new Date(idv.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : ""} · {idv?.filename}
        </p>
      )}
    </div>
  );
}

function OrdersSection({ orders }) {
  return (
    <div className="mt-16" data-testid="account-orders-section">
      <div className="flex items-center gap-2 text-[var(--twr-bronze)]">
        <Package size={14} strokeWidth={1.5} />
        <p className="eyebrow">Your Orders</p>
      </div>
      {orders.length === 0 ? (
        <div className="surface p-8 mt-5 max-w-2xl">
          <p className="text-[var(--twr-warm-white)]/60 text-sm">No orders yet. The humidor is stocked.</p>
          <Link to="/shop" className="btn-ghost mt-5 inline-flex">Enter the Shop <ChevronRight size={13} /></Link>
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
              <tr>
                <th className="text-left px-5 py-4">Date</th>
                <th className="text-left px-5 py-4">Items</th>
                <th className="text-left px-5 py-4">Total</th>
                <th className="text-right px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/75">{new Date(o.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}</td>
                  <td className="px-5 py-4"><div className="max-w-md truncate">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</div></td>
                  <td className="px-5 py-4 font-serif text-[var(--twr-bronze)]">${Number(o.amount || 0).toFixed(2)}</td>
                  <td className="px-5 py-4 text-right">
                    <span className={`text-[11px] tracking-[0.18em] uppercase ${o.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{o.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nxt, setNxt] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await memberApi.post("/account/change-password", { current_password: cur, new_password: nxt });
      toast.success("Password updated.");
      setOpen(false); setCur(""); setNxt("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="account-change-pw-btn" className="inline-flex items-center gap-2 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors">
          <Key size={13} /> Change Password
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-left">Change password</DialogTitle>
          <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="eyebrow">Current Password</span>
            <input data-testid="account-current-pw" type="password" required value={cur} onChange={(e) => setCur(e.target.value)}
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="eyebrow">New Password (8+ chars)</span>
            <input data-testid="account-new-pw" type="password" required minLength={8} value={nxt} onChange={(e) => setNxt(e.target.value)}
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
          </label>
          <button disabled={busy} data-testid="account-change-pw-submit" className="btn-bronze w-full">
            {busy ? "Updating…" : "Update Password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
